import Icon from '../../components/Icon';
import { FIELD_ICON } from './fieldMeta';
import type { FormField } from '../../types';

/**
 * What the rep will see.
 *
 * The point of this panel is that the admin is building a screen for
 * somebody else, standing outside a shop on a phone. Seeing the length
 * of the form as it grows is the whole feedback loop — a 22-field form
 * looks reasonable as a list in the middle column and obviously wrong
 * here.
 */

function Control({ field }: { field: FormField }) {
  switch (field.type) {
    case 'textarea':
      return <div className="pv-control tall">{field.placeholder || 'Type here'}</div>;

    case 'boolean':
      return (
        <div className="pv-control" style={{ justifyContent: 'space-between' }}>
          <span>No</span>
          <span className="switch-track" style={{ transform: 'scale(0.8)' }} />
        </div>
      );

    case 'select':
      return (
        <div className="pv-control" style={{ justifyContent: 'space-between' }}>
          <span>{field.options?.[0]?.label ?? 'Choose'}</span>
          <Icon name="chevron-down" size={13} />
        </div>
      );

    case 'multiselect':
      return (
        <div className="row wrap gap-4">
          {(field.options ?? []).slice(0, 4).map((o, i) => (
            <span className="pv-chip" key={i}>{o.label}</span>
          ))}
          {(field.options?.length ?? 0) > 4 ? <span className="pv-chip">+more</span> : null}
        </div>
      );

    case 'photo':
      return (
        <div className="pv-control tall" style={{ justifyContent: 'center', alignItems: 'center' }}>
          <Icon name="camera" size={16} /> Take photo
        </div>
      );

    case 'geo':
      return (
        <div className="pv-control" style={{ justifyContent: 'center' }}>
          <Icon name="pin" size={14} /> Capture shop location
        </div>
      );

    case 'route':
      return (
        <div className="pv-control" style={{ justifyContent: 'space-between' }}>
          <span>Pick an area</span>
          <Icon name="chevron-down" size={13} />
        </div>
      );

    case 'date':
      return (
        <div className="pv-control" style={{ justifyContent: 'space-between' }}>
          <span>DD / MM / YYYY</span>
          <Icon name="date" size={13} />
        </div>
      );

    case 'gstin':
      return <div className="pv-control">{field.placeholder || '27AAPFU0939F1ZV'}</div>;

    case 'pan':
      return <div className="pv-control">{field.placeholder || 'AAPFU0939F'}</div>;

    case 'phone':
      return (
        <div className="pv-control">
          <span style={{ color: 'var(--ink-muted)' }}>+91</span>
          {field.placeholder || '98765 43210'}
        </div>
      );

    default:
      return (
        <div className="pv-control">
          <Icon name={FIELD_ICON[field.type]} size={13} />
          {field.placeholder || 'Type here'}
        </div>
      );
  }
}

export default function PhonePreview({
  fields, chrome = { title: 'New Customer', cta: 'Save customer' },
}: { fields: FormField[]; chrome?: { title: string; cta: string } }) {
  const requiredCount = fields.filter((f) => f.required).length;

  return (
    <div className="card">
      <div className="card-head">
        <h3>What the rep sees</h3>
        <span className="small faint">{fields.length} fields</span>
      </div>

      <div style={{ padding: '16px 0 18px' }}>
        <div className="phone">
          <div className="phone-notch" />
          <div className="phone-screen">
            <div className="phone-title">{chrome.title}</div>

            {fields.map((field) => (
              <div className="pv-field" key={field.key}>
                <span className="pv-label">
                  {field.label || 'Untitled'}
                  {field.required ? <span className="pv-req"> *</span> : null}
                </span>
                <Control field={field} />
                {field.helpText ? <div className="pv-help">{field.helpText}</div> : null}
              </div>
            ))}

            <div className="pv-cta">{chrome.cta}</div>
          </div>
        </div>
      </div>

      {fields.length > 10 ? (
        <div style={{ padding: '0 16px 16px' }}>
          <div className="banner banner-warning">
            <Icon name="alert" size={16} />
            <div>
              {fields.length} fields is a long form to fill in standing outside a shop.
              {requiredCount > 6
                ? ` ${requiredCount} of them are required.`
                : ' Consider which of these you really need on every visit.'}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
